import type { RunAggregateProjectionV2, WebTestRun } from '@blume/web-test-model'
import { useMemo } from 'react'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { relativeTime } from '@/design/format'
import { RunStatusBadge } from '@/design/status'
import { AvatarTile, scenarioEmoji } from '@/ui/atoms/avatar-tile'
import { titleCase } from '@/logic'

import {
  scenarioHref,
  scenarioStatus,
  scenarioSummaries,
  type ScenarioSummary
} from './entity-shared'
import { EmptyState, MetaLine, PageTitle } from './parts'

/** Compact, honest context rather than a sprawling many-to-many membership list. */
function SuiteContext({ scenario }: { scenario: ScenarioSummary }) {
  const unloaded = Math.max(0, scenario.runCount - scenario.loadedCount)
  if (scenario.loadedCount === 0) {
    return (
      <span className="text-[13px] text-muted-foreground">
        Membership unknown — none of the {scenario.runCount} run
        {scenario.runCount === 1 ? ' is' : 's are'} loaded
      </span>
    )
  }
  return (
    <span className="text-[13px] text-muted-foreground">
      {scenario.suiteRunIds.length === 0
        ? 'No suite runs observed'
        : `${scenario.suiteRunIds.length} suite${scenario.suiteRunIds.length === 1 ? '' : 's'} observed`}
      {scenario.latestSuiteRunId ? (
        <>
          {' · latest '}
          <span className="font-mono text-foreground">{scenario.latestSuiteRunId}</span>
        </>
      ) : null}
      {unloaded > 0 ? (
        <>
          {' '}
          · {unloaded} earlier run{unloaded === 1 ? '' : 's'} not loaded
        </>
      ) : null}
    </span>
  )
}

function openScenario(scenarioId: string): void {
  window.location.hash = scenarioHref(scenarioId)
}

export function ScenariosPage({
  runs,
  aggregates,
  now
}: {
  runs: WebTestRun[]
  aggregates?: RunAggregateProjectionV2[]
  now: number
}) {
  const scenarios = useMemo(() => scenarioSummaries(runs, aggregates), [aggregates, runs])

  return (
    <>
      <PageTitle
        title="Scenarios"
        subtitle="Reusable test definitions — each scenario can belong to several suites and can also run ad-hoc."
      />
      {scenarios.length === 0 ? (
        <EmptyState>No scenarios have run yet.</EmptyState>
      ) : (
        <>
          {/*
           * Wide and tablet layout: a compact suite-context column avoids
           * turning an index into a sprawling many-to-many membership list.
           * — hairline row separators only. A row is the one link-equivalent
           * interaction, so it does not contain a competing trailing action.
           */}
          <div className="hidden max-w-[70rem] md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scenario</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Suite context</TableHead>
                  <TableHead className="text-right">Runs</TableHead>
                  <TableHead>Last run</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scenarios.map((scenario) => {
                  const status = scenarioStatus(scenario)
                  return (
                    <TableRow
                      key={scenario.scenarioId}
                      role="link"
                      tabIndex={0}
                      aria-label={`Open scenario ${scenario.scenarioId}`}
                      onClick={() => openScenario(scenario.scenarioId)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          openScenario(scenario.scenarioId)
                        }
                      }}
                      className="cursor-pointer transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
                    >
                      <TableCell className="py-3 whitespace-normal">
                        <span className="flex items-center gap-2">
                          <AvatarTile
                            id={scenario.scenarioId}
                            emoji={scenarioEmoji(scenario.scenarioId)}
                            size="regular"
                          />
                          <span className="min-w-0">
                            <span className="block font-mono text-[13px] font-semibold break-words">
                              {scenario.scenarioId}
                            </span>
                            <span className="mt-0.5 block">
                              <MetaLine
                                items={[
                                  scenario.targets.length > 0
                                    ? scenario.targets.map(titleCase).join(', ')
                                    : undefined,
                                  scenario.hasAdHoc ? 'Ad-hoc run observed' : undefined
                                ]}
                              />
                            </span>
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <RunStatusBadge meta={status.meta} active={status.active} />
                      </TableCell>
                      <TableCell className="max-w-64 py-3 whitespace-normal">
                        <SuiteContext scenario={scenario} />
                      </TableCell>
                      <TableCell className="py-3 text-right text-[13px] tabular-nums">
                        {scenario.runCount}
                      </TableCell>
                      <TableCell className="py-3 text-[13px] text-muted-foreground">
                        {relativeTime(scenario.updatedAt, now)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
          {/*
           * Narrow layout (390px): the same information stacked, separated by
           * hairline dividers. Suite membership keeps its explicit label as a
           * definition-list field, chips wrap instead of scrolling sideways,
           * and the entire item is a single touch target.
           */}
          <ul className="max-w-[43rem] divide-y divide-border md:hidden">
            {scenarios.map((scenario) => {
              const status = scenarioStatus(scenario)
              return (
                <li key={scenario.scenarioId}>
                  <a
                    href={scenarioHref(scenario.scenarioId)}
                    className="-mx-3 block rounded-md px-3 py-4 transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none first:pt-0"
                    aria-label={`Open scenario ${scenario.scenarioId}`}
                  >
                    <div className="flex items-center gap-2">
                      <AvatarTile
                        id={scenario.scenarioId}
                        emoji={scenarioEmoji(scenario.scenarioId)}
                        size="regular"
                      />
                      <div className="min-w-0">
                        <p className="font-mono text-[13px] font-semibold break-words">
                          {scenario.scenarioId}
                        </p>
                        <p className="mt-1">
                          <RunStatusBadge meta={status.meta} active={status.active} />
                        </p>
                      </div>
                    </div>
                    <dl className="mt-2.5">
                      <dt className="text-xs font-medium tracking-[0.08em] text-muted-foreground uppercase">
                        Suite context
                      </dt>
                      <dd className="mt-1">
                        <SuiteContext scenario={scenario} />
                      </dd>
                    </dl>
                    <p className="mt-2">
                      <MetaLine
                        items={[
                          scenario.targets.length > 0
                            ? scenario.targets.map(titleCase).join(', ')
                            : undefined,
                          scenario.hasAdHoc ? 'Ad-hoc run observed' : undefined,
                          `${scenario.runCount} run${scenario.runCount === 1 ? '' : 's'}`,
                          `last run ${relativeTime(scenario.updatedAt, now)}`
                        ]}
                      />
                    </p>
                  </a>
                </li>
              )
            })}
          </ul>
          <p className="mt-4 max-w-[46rem] text-[13px] text-muted-foreground">
            A scenario is one reusable definition: it can belong to several suites at once, and a
            run can also start ad-hoc outside any suite. To keep this inventory readable, suite
            context is summarized from loaded runs; it is not an exhaustive membership list.
          </p>
        </>
      )}
    </>
  )
}
